import FormSelect from 'components/form/FormSelect';
import FormText from 'components/form/FormText';
import IndicatorButton from 'components/indicator/IndicatorButton';
import { AppConst } from 'constants/appConst';
import { AppHelper } from 'helpers/appHelper';
import { useAppState } from 'hooks/useAppState';
import { useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { AcctState } from 'stores/atoms/acctState';

/**
 *　アカウント登録画面
 */
export default function Signup(): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */
  type FormStateType = {
    mailAddress: string;
    fullName: string;
    companyName: string;
    tel: string;
    zipCode: string;
    prefectureDiv: string;
    city: string;
    address: string;
    buildingName: string;
  };

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();
  const navigate = useNavigate();

  const { signUpRequest } = useAppState();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [acctState] = useRecoilState(AcctState);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormStateType>();

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    // ログイン済の場合は、カンパニー選択画面に遷移
    if (acctState.user) {
      navigate('/signin/company');
    }
  }, []);

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   *　登録ボタン押下時の処理
   */
  const onSubmit = () =>
    AppHelper.submit(
      processing,
      setProcessing,
      handleSubmit(async (data: FormStateType) => {
        try {
          // サインアップリクエストを送信
          await signUpRequest(data.mailAddress);
          setError(null);

          // ログイン認証コード入力画面に遷移
          navigate('/signin/auth', {
            state: {
              mailAddress: data.mailAddress,
              nextTo: '/',
              signupData: data,
            },
          });
        } catch (e) {
          if (e.name === 'UsernameExistsException') {
            setError('すでに登録されているメールアドレスです。');
          } else {
            showBoundary(e);
          }
        }
      })
    );

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      <section className="section-header">
        <div className="container">
          <div className="d-flex justify-content-center">
            <div className="text-center" style={{ marginTop: '3rem' }}>
              <img
                src="/img/common/logo.svg"
                alt="BranQ ブランク"
                className="d-block mx-auto mb-4"
                style={{ maxWidth: '200px' }}
              />
              <h2 className="flex-title">無料でトライアルをはじめる。</h2>
            </div>
          </div>
        </div>
      </section>
      <section className="section-basic-setting">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-sm-8 col-12">
              <div className="box-common pb-1">
                <form className="form-profile g-3">
                  <div className="form-group m-0 mb-5">
                    {/* ユーザー情報セクション */}
                    <div className="mt-1">
                      <div className="row justify-content-center">
                        <div className="row form-row flex-md-row justify-content-between">
                          <div className="column col-12">
                            <h3 className="title-section title-md fw-bold mb-3">ユーザー情報</h3>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* メールアドレス・氏名 */}
                    <div className="row justify-content-center">
                      <div className="row form-row flex-md-row justify-content-between">
                        <div className="column col-lg-6 col-xl-6 col-left">
                          <FormText
                            subject="メールアドレス"
                            name="mailAddress"
                            placeholder="example@example.com"
                            register={register}
                            errors={errors}
                            required={true}
                            maxLength={300}
                            format="email"
                            requiredMark={true}
                          />
                        </div>
                        <div className="column col-lg-6 col-xl-6 col-right">
                          <FormText
                            subject="氏名"
                            name="fullName"
                            placeholder="山田 太郎"
                            register={register}
                            errors={errors}
                            required={true}
                            maxLength={100}
                            requiredMark={true}
                          />
                        </div>
                      </div>
                    </div>
                    {/* 会社情報セクション */}
                    <div className="mt-5">
                      <div className="row justify-content-center">
                        <div className="row form-row flex-md-row justify-content-between">
                          <div className="column col-12">
                            <h3 className="title-section title-md fw-bold mb-3">会社情報</h3>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* 法人名・電話番号 */}
                    <div className="row justify-content-center">
                      <div className="row form-row flex-md-row justify-content-between">
                        <div className="column col-lg-6 col-xl-6 col-left">
                          <FormText
                            subject="法人名"
                            name="companyName"
                            placeholder="株式会社BranQ"
                            register={register}
                            errors={errors}
                            required={true}
                            maxLength={100}
                            requiredMark={true}
                          />
                        </div>
                        <div className="column col-lg-6 col-xl-6 col-right">
                          <FormText
                            subject="電話番号"
                            name="tel"
                            placeholder="1234567890"
                            register={register}
                            errors={errors}
                            required={true}
                            maxLength={11}
                            format="numeric"
                            requiredMark={true}
                          />
                        </div>
                      </div>
                    </div>
                    {/* 郵便番号・都道府県 */}
                    <div className="row justify-content-center">
                      <div className="row form-row flex-md-row justify-content-between">
                        <div className="column col-lg-6 col-xl-6 col-left">
                          <FormText
                            subject="郵便番号"
                            name="zipCode"
                            placeholder="1000001"
                            register={register}
                            errors={errors}
                            required={true}
                            maxLength={7}
                            minLength={7}
                            format="numeric"
                            requiredMark={true}
                          />
                        </div>
                        <div className="column col-lg-6 col-xl-6 col-right">
                          <FormSelect
                            subject="都道府県"
                            name="prefectureDiv"
                            register={register}
                            errors={errors}
                            options={AppHelper.getDivSelectOptions('prefecture')}
                            required={true}
                            blank={true}
                            blankLabel="選択してください"
                            requiredMark={true}
                          />
                        </div>
                      </div>
                    </div>
                    {/* 市区町村・番地以降 */}
                    <div className="row justify-content-center">
                      <div className="row form-row flex-md-row justify-content-between">
                        <div className="column col-lg-6 col-xl-6 col-left">
                          <FormText
                            subject="市区町村"
                            name="city"
                            placeholder="千代田区"
                            register={register}
                            errors={errors}
                            required={true}
                            maxLength={100}
                            requiredMark={true}
                          />
                        </div>
                        <div className="column col-lg-6 col-xl-6 col-right">
                          <FormText
                            subject="番地以降"
                            name="address"
                            placeholder="丸の内1-1-1"
                            register={register}
                            errors={errors}
                            required={true}
                            maxLength={100}
                            requiredMark={true}
                          />
                        </div>
                      </div>
                    </div>
                    {/* 建物名・部屋番号 */}
                    <div className="row justify-content-center">
                      <div className="row form-row flex-md-row justify-content-between">
                        <div className="column col-lg-6 col-xl-6 col-left">
                          <FormText
                            subject="建物名・部屋番号"
                            name="buildingName"
                            placeholder="サンプルビル1F"
                            register={register}
                            errors={errors}
                            maxLength={100}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="mt-7" style={{ marginTop: '5rem' }}>
                      <div className="mt-7 d-flex justify-content-center">
                        <a href={AppConst.url.term} target="_blank" rel="noopener noreferrer">
                          利用規約
                        </a>
                      </div>
                      <div className="mt-7 d-flex justify-content-center">
                        <a href={AppConst.url.privacy} target="_blank" rel="noopener noreferrer">
                          プライバシーポリシー
                        </a>
                      </div>
                    </div>
                    {/* 登録ボタン */}
                    <div className="mt-5">
                      <div className="form-footer mb-2">
                        {' '}
                        <div className="row justify-content-center">
                          <div className="col-12 col-md-8 col-lg-6">
                            <button
                              type="button"
                              className="btn btn-common moderate-blue w-100"
                              onClick={onSubmit}
                              disabled={processing}
                            >
                              <IndicatorButton visible={processing} />
                              利用規約とプライバシーポリシーに同意してはじめる
                            </button>
                            {error && (
                              <div className="mt-2 text-danger text-center fw-bold">{error}</div>
                            )}
                            <div className="mt-3 text-center">
                              <div className="d-flex flex-wrap justify-content-center align-items-center gap-2">
                                <p className="m-0 text-nowrap">BranQのアカウントをお持ちですか？</p>
                                <a
                                  onClick={() => navigate('/signin')}
                                  rel="noopener noreferrer"
                                  className="d-flex align-items-center m-0 text-nowrap"
                                >
                                  ログインはこちら
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* コピーライト */}
      <div className="mt-2 text-center mb-2">
        <small className="text-muted">©BranQ Inc.</small>
      </div>
    </>
  );
}
